import React, { useEffect } from 'react';

import type { RenderModelDocument, RenderModelFooter, RenderModelHeader } from '@aline/common';
import { AlineTransforms, capitalize, FooterType, HeaderType } from '@aline/common';
import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { Divider, FormControlLabel, Typography } from '@mui/material';
import { equals } from 'ramda';
import type { NodeEntry, Point } from 'slate';
import { Editor } from 'slate';
import { AlineCheckbox, Spacer } from 'src/components';
import {
  useEditorTabStateActions,
  useEditorTabStateSelector,
} from 'src/editor/editor-tab-state-registry';
import { Queries } from 'src/editor/queries';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor, useHeaderOrFooterOpen, useSlateSelector } from 'src/layout-engine/hooks';
import { useEditingDisabled } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

interface StyleProps {
  type: 'header' | 'footer';
}

const useStyles = makeStyles<StyleProps>()((theme, { type }) => ({
  root: {
    left: 0,
    [type === 'header' ? 'top' : 'bottom']: 0,
    position: 'absolute',
    right: 0,
    zIndex: theme.appZIndex.header,
  },
}));

const openHeaderOrFooterPageNumVar = makeVar<number | null>(null);

export function useOpenHeaderOrFooterPageNum() {
  return useReactiveVar(openHeaderOrFooterPageNumVar);
}

interface HeaderOrFooterProps {
  pageNumber: number;
  rMDoc: RenderModelDocument;
  type: 'header' | 'footer';
  onDoubleClick?: (event: React.MouseEvent) => void;
  onMouseDown?: (event: React.MouseEvent) => void;
}

export function HeaderOrFooter({
  onDoubleClick,
  onMouseDown,
  pageNumber,
  rMDoc,
  type,
}: HeaderOrFooterProps) {
  const editor = useEditor();
  const isHeader = type === 'header';
  const isOpen = useHeaderOrFooterOpen(type);
  const activePageDimensions = Queries.activePageDimensions(editor, 'in');

  const rMNodeEntry = useSlateSelector(
    () =>
      Queries.getRMHeaderOrFooterEntryOnPage(editor, {
        pageNumber,
        rMDocProps: rMDoc,
        type,
      }),
    equals
  );

  const height = Queries.renderedHeaderOrFooterHeight(
    {
      activePageDimensions,
      rMElement: rMNodeEntry[0],
      rMPart: isHeader ? rMDoc.headers : rMDoc.footers,
    },
    'px'
  );

  const { classes } = useStyles({ type });

  useEffect(() => {
    if (!isOpen) openHeaderOrFooterPageNumVar(null);
  }, [isOpen]);

  return (
    <div
      className={classes.root}
      style={{ height }}
      onDoubleClick={event => {
        if (isOpen) return;
        onDoubleClick?.(event);
        openHeaderOrFooterPageNumVar(pageNumber);
      }}
      onMouseDown={onMouseDown}
    >
      {isOpen && (
        <MenuBar pageNumber={pageNumber} rMDoc={rMDoc} rMNodeEntry={rMNodeEntry} type={type} />
      )}
    </div>
  );
}

interface MenuBarStyleProps {
  type: 'header' | 'footer';
}

const useMenuBarStyles = makeStyles<MenuBarStyleProps>()((theme, { type }) => ({
  divider: {
    height: 16,
    margin: theme.spacing(0, 2),
  },
  formControl: {
    alignItems: 'unset',
    display: 'unset',
    marginRight: 'unset',
  },
  label: {
    ...theme.appTypography.fieldLabel,
  },
  menuBar: {
    alignItems: 'center',
    backgroundColor: theme.appColors.background,
    [type === 'header' ? 'bottom' : 'top']: 0,
    boxSizing: 'border-box',
    display: 'flex',
    height: 32,
    padding: theme.spacing(0.5, 4, 0.5, 2),
    position: 'absolute',
    transform: type === 'header' ? 'translateY(100%)' : 'translateY(-100%)',
    width: '100%',
  },
  optionsText: {
    ...theme.appTypography.buttonSmall,
    cursor: 'pointer',
  },
}));

interface MenuBarProps {
  pageNumber: number;
  rMDoc: RenderModelDocument;
  rMNodeEntry: NodeEntry<RenderModelHeader | RenderModelFooter>;
  type: 'header' | 'footer';
}

function MenuBar({ pageNumber, rMDoc, rMNodeEntry, type }: MenuBarProps) {
  const [rMNode] = rMNodeEntry;

  const editor = useEditor();
  const cannotEdit = useEditingDisabled();
  const currentUserId = useCurrentUserId();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);
  const {
    documentUi: { setHeaderFooterDialogOpen },
  } = useEditorTabStateActions();
  const diffFirstPage = useEditorTabStateSelector(
    state => state.derived.headerFooterProperties.diffFirstPage
  );
  const { classes } = useMenuBarStyles({ type });

  const { evenOdd } = Queries.activeHeaderOrFooterProps(rMDoc);
  const isHeader = type === 'header';
  const isFirstPage = pageNumber === 1;

  const firstPageType = isHeader ? HeaderType.FirstPage : FooterType.FirstPage;
  const evenType = isHeader ? HeaderType.Even : FooterType.Even;
  const defaultType = isHeader ? HeaderType.Default : FooterType.Default;

  const label =
    rMNode.type === firstPageType
      ? `First page ${type}`
      : rMNode.type === evenType
        ? `Even page ${type}`
        : rMNode.type === defaultType && evenOdd
          ? `Odd page ${type}`
          : capitalize(type);

  function getSelectPointOnToggle(checked: boolean): Point | void {
    const partEntry = isHeader
      ? Queries.getHeadersElement(editor)
      : Queries.getFootersElement(editor);
    if (!partEntry) return;

    const typeToSelect = checked ? firstPageType : defaultType;

    const [, path] = Queries.getHeaderOrFooter({
      editor,
      partEntry: partEntry,
      type: typeToSelect,
    });

    return Editor.start(editor, path);
  }

  return (
    <div
      className={classes.menuBar}
      onMouseDown={stopEvent}
      onMouseMove={stopEvent}
      onMouseUp={stopEvent}
    >
      <Typography className={classes.label}>{label}</Typography>
      <Spacer />
      {isFirstPage && (
        <>
          <FormControlLabel
            className={classes.formControl}
            control={
              <AlineCheckbox
                disableHoverBackground
                checked={diffFirstPage}
                color="primary"
                disabled={cannotEdit}
                onChange={(_event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
                  AlineTransforms.withoutNormalizing(editor, () => {
                    if (!currentUserId || !editingMode) return;

                    AuthorizedTransforms.setDiffFirstPageHeaderFooter(editor, {
                      authorId: currentUserId,
                      diffFirstPage: checked,
                      editingMode,
                    });

                    const selectPoint = getSelectPointOnToggle(checked);
                    if (selectPoint) AlineTransforms.select(editor, selectPoint);

                    ClientTransforms.focus(editor);
                  });
                }}
              />
            }
            label={<Typography variant="caption">Different first page</Typography>}
          />
          <Divider className={classes.divider} orientation="vertical" />
        </>
      )}
      <Typography
        className={classes.optionsText}
        onClick={(): void => {
          setHeaderFooterDialogOpen(true);
        }}
      >
        Options
      </Typography>
    </div>
  );
}

function stopEvent(event: React.MouseEvent): void {
  event.preventDefault();
  event.stopPropagation();
}
